import { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Users, CreditCard, Gift, Clock, RefreshCw, TrendingUp, UserPlus, AlertCircle, Eye, Search, UserCog, ShieldOff } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader } from "../components/Crud";

/**
 * Lightweight founder-only launch dashboard. Deliberately minimal — no
 * revenue, MRR, AI usage or health metrics. Auto-refreshes every 60 s and
 * exposes a manual refresh button. Founder gate enforced client-side plus
 * server-side (`/api/admin/snapshot` returns 403 for non-founders).
 */
export default function Founder() {
    const { user, loading, viewAs, setViewAs, startImpersonation } = useAuth();
    const nav = useNavigate();
    const [snap, setSnap] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [refreshedAt, setRefreshedAt] = useState(null);

    const load = useCallback(async () => {
        setBusy(true); setErr("");
        try {
            const { data } = await api.get("/admin/snapshot");
            setSnap(data);
            setRefreshedAt(new Date());
        } catch (e) {
            setErr(e?.response?.data?.detail || "Could not load snapshot.");
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => {
        if (!user?.is_founder) return undefined;
        load();
        // Auto-refresh every 60 s per spec — cheap query, so this is fine.
        const id = setInterval(load, 60_000);
        return () => clearInterval(id);
    }, [user?.is_founder, load]);

    if (loading) return null;
    if (!user) return <Navigate to="/login" replace />;
    if (!user.is_founder) return <Navigate to="/dashboard" replace />;

    const totals = snap?.totals || {};
    const today = snap?.today || {};
    const activity = snap?.recent_activity || [];
    const cr = snap ? (snap.conversion_rate * 100) : 0;

    return (
        <div data-testid="founder-page">
            <PageHeader
                title="Founder"
                subtitle="today's snapshot"
                action={
                    <div className="flex items-center gap-2">
                        {refreshedAt && (
                            <span className="text-xs text-[var(--text-ink)]/55" data-testid="founder-refreshed-at">
                                {formatRelative(refreshedAt)}
                            </span>
                        )}
                        <button onClick={load} disabled={busy}
                                data-testid="founder-refresh-btn"
                                className="btn-ghost text-sm inline-flex items-center gap-1.5">
                            <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
                            Refresh
                        </button>
                    </div>
                } />

            {err && (
                <div className="card-warm border-l-4 border-red-500/70 p-3 mb-4 text-sm flex items-center gap-2" data-testid="founder-error">
                    <AlertCircle size={14} className="text-red-500" /> {err}
                </div>
            )}

            {snap && snap.stripe_available === false && (
                <div className="card-warm border-l-4 border-amber-500/70 p-3 mb-4 text-sm flex items-start gap-2" data-testid="founder-stripe-warning">
                    <AlertCircle size={14} className="text-amber-600 mt-0.5 shrink-0" />
                    <div>
                        <div className="font-medium">Stripe unavailable</div>
                        <div className="text-[var(--text-ink)]/70 text-xs mt-0.5">
                            Paying subscriber count could not be verified against Stripe. Showing 0 until Stripe responds — retry in a moment.
                        </div>
                    </div>
                </div>
            )}

            {/* Today's Snapshot */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6 fade-up">
                <SnapCard icon={Users} label="Registered users"
                          value={totals.registered_users} testid="snap-registered" />
                <SnapCard icon={CreditCard} label="Paying subscribers"
                          value={totals.paying_subscribers} testid="snap-paying"
                          accent="brand" />
                <SnapCard icon={Gift} label="Active free trials"
                          value={totals.active_trials} testid="snap-trials" />
                <SnapCard icon={Clock} label="Expired trials"
                          value={totals.expired_trials} testid="snap-expired" />
            </div>

            {/* View-As mode switcher — founder-only projection, no DB writes. */}
            <ViewAsSwitcher realRole={user.role} viewAs={viewAs} setViewAs={setViewAs} onSwitch={() => nav("/dashboard")} />

            {/* Impersonation support tool — search + short-lived session start. */}
            <ImpersonatePanel onStarted={(payload) => {
                startImpersonation(payload).then(() => nav("/dashboard"));
            }} />

            {/* User Management — list all users with two ops actions. */}
            <UserManagementPanel />

            {/* Today */}
            <div className="card-warm p-5 mb-6" data-testid="founder-today">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-[var(--text-ink)]/55">Today</div>
                        <div className="font-serif text-lg leading-tight">Signups &amp; new subscribers</div>
                    </div>
                    <div className="text-xs text-[var(--text-ink)]/55 flex items-center gap-1.5" data-testid="snap-conversion-rate">
                        <TrendingUp size={12} /> Conversion&nbsp;
                        <span className="font-medium tabular-nums text-[var(--text-ink)]">
                            {cr.toFixed(cr < 1 ? 2 : 1)}%
                        </span>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <MiniStat icon={UserPlus} label="New users" value={today.new_users} testid="today-new-users" />
                    <MiniStat icon={CreditCard} label="New subscribers" value={today.new_subscribers} testid="today-new-subs" />
                </div>
            </div>

            {/* Recent activity */}
            <div className="card-warm p-5" data-testid="founder-activity">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-[var(--text-ink)]/55">Recent activity</div>
                        <div className="font-serif text-lg leading-tight">Last events</div>
                    </div>
                    <div className="text-xs text-[var(--text-ink)]/55 tabular-nums" data-testid="founder-activity-count">
                        {activity.length} events
                    </div>
                </div>
                {activity.length === 0 ? (
                    <div className="text-sm text-[var(--text-ink)]/55" data-testid="founder-activity-empty">
                        No activity yet.
                    </div>
                ) : (
                    <ul className="divide-y divide-[rgba(26,19,24,0.06)] -mx-2">
                        {activity.map((e, i) => (
                            <li key={i} className="px-2 py-2 flex items-center gap-3 min-w-0"
                                data-testid={`founder-event-${i}`}>
                                <EventDot type={e.type} />
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm truncate" data-testid={`founder-event-${i}-email`}>
                                        {e.email}
                                    </div>
                                    <div className="text-[11px] text-[var(--text-ink)]/60 truncate">
                                        {e.label}
                                    </div>
                                </div>
                                <div className="text-[11px] text-[var(--text-ink)]/55 tabular-nums shrink-0"
                                     data-testid={`founder-event-${i}-time`}>
                                    {formatEventTime(e.ts)}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function SnapCard({ icon: Icon, label, value, testid, accent }) {
    return (
        <div className={`rounded-2xl px-4 py-3 border ${
            accent === "brand"
                ? "brand-gradient text-white border-transparent"
                : "border-[rgba(26,19,24,0.08)] bg-white/60"
        }`}
             data-testid={testid}>
            <div className="flex items-center gap-2">
                <Icon size={14} className={accent === "brand" ? "text-white" : "text-[var(--brand-purple)]"} />
                <div className={`text-[10px] uppercase tracking-wider ${accent === "brand" ? "text-white/85" : "text-[var(--text-ink)]/55"}`}>
                    {label}
                </div>
            </div>
            <div className="font-serif text-2xl mt-1 leading-none tabular-nums">
                {value ?? "—"}
            </div>
        </div>
    );
}

function MiniStat({ icon: Icon, label, value, testid }) {
    return (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-[rgba(26,19,24,0.04)]" data-testid={testid}>
            <div className="w-9 h-9 rounded-full bg-white grid place-items-center shrink-0">
                <Icon size={14} className="text-[var(--brand-purple)]" />
            </div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-ink)]/55">{label}</div>
                <div className="font-serif text-xl leading-none tabular-nums">+{value ?? 0}</div>
            </div>
        </div>
    );
}

const _EVENT_COLORS = {
    registration:          "bg-blue-400",
    trial_started:         "bg-amber-400",
    subscription_started:  "bg-emerald-500",
    trial_expired:         "bg-rose-400",
    subscription_cancelled:"bg-rose-500",
};

function EventDot({ type }) {
    return (
        <span className={`w-2 h-2 rounded-full shrink-0 ${_EVENT_COLORS[type] || "bg-[var(--brand-purple)]"}`} />
    );
}

function formatEventTime(iso) {
    try {
        const d = new Date(iso);
        const now = new Date();
        const diffMin = Math.floor((now - d) / 60000);
        if (diffMin < 1) return "just now";
        if (diffMin < 60) return `${diffMin}m ago`;
        const diffH = Math.floor(diffMin / 60);
        if (diffH < 24) return `${diffH}h ago`;
        const diffD = Math.floor(diffH / 24);
        if (diffD < 7) return `${diffD}d ago`;
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return ""; }
}

function formatRelative(d) {
    const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    if (diffSec < 5) return "Just refreshed";
    if (diffSec < 60) return `Refreshed ${diffSec}s ago`;
    return `Refreshed ${Math.floor(diffSec / 60)}m ago`;
}


// ─────────────────────────────────────────────────────────────────────── //
// Founder tools: "View As" mode + Impersonation
// ─────────────────────────────────────────────────────────────────────── //

/**
 * Segmented control that flips the founder's sidebar + dashboard between
 * Professional and Student. Purely UI — never persisted to the DB, never
 * changes subscription, never leaks to any other user. Choice survives
 * across sessions via localStorage.
 */
function ViewAsSwitcher({ realRole, viewAs, setViewAs, onSwitch }) {
    const active = viewAs || realRole;
    const pick = (role) => {
        setViewAs(role === realRole ? null : role); // null means "back to my own role"
        onSwitch?.();
    };
    return (
        <div className="card-warm p-4 mb-6" data-testid="view-as-card">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg brand-gradient grid place-items-center text-white shrink-0">
                        <Eye size={16} />
                    </div>
                    <div>
                        <div className="font-serif text-base leading-tight">View As</div>
                        <div className="text-xs text-[var(--text-ink)]/60">
                            Preview the app as a Student or Professional. Only affects your view.
                        </div>
                    </div>
                </div>
                <div className="inline-flex rounded-full border border-[rgba(26,19,24,0.12)] p-0.5 bg-white/60" role="tablist">
                    {[
                        { key: "student",      label: "Student" },
                        { key: "professional", label: "Professional" },
                    ].map((opt) => (
                        <button
                            key={opt.key}
                            type="button"
                            role="tab"
                            aria-selected={active === opt.key}
                            data-testid={`view-as-${opt.key}`}
                            onClick={() => pick(opt.key)}
                            className={`px-3.5 py-1.5 text-xs rounded-full transition ${
                                active === opt.key
                                    ? "brand-gradient text-white"
                                    : "text-[var(--text-ink)]/70 hover:bg-[rgba(26,19,24,0.05)]"
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>
            {viewAs && viewAs !== realRole && (
                <div className="text-[11px] text-[var(--brand-orange)] mt-2 pl-11" data-testid="view-as-note">
                    You&apos;re viewing the workspace as a {viewAs}. Your account type on file is still <b>{realRole}</b>.
                </div>
            )}
        </div>
    );
}

/**
 * Founder-only user search + impersonate control. Debounced search, guards
 * against impersonating another founder, and starts a signed short-lived
 * session by calling ``POST /admin/impersonate/{user_id}``.
 */
function ImpersonatePanel({ onStarted }) {
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    // Debounced search — 250 ms lets a fast typer land keystrokes without
    // firing a query per character.
    useEffect(() => {
        setErr("");
        if (!q.trim()) { setRows([]); return; }
        const t = setTimeout(async () => {
            setBusy(true);
            try {
                const { data } = await api.get("/admin/users/search", { params: { q, limit: 20 } });
                setRows(data.results || []);
            } catch (e) {
                setErr(e?.response?.data?.detail || "Search failed");
            } finally {
                setBusy(false);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [q]);

    const impersonate = async (row) => {
        if (row.blocked) return;
        setErr("");
        try {
            const { data } = await api.post(`/admin/impersonate/${row.user_id}`);
            onStarted?.(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Could not start impersonation");
        }
    };

    return (
        <div className="card-warm p-4 mb-6" data-testid="impersonate-card">
            <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 rounded-lg grid place-items-center text-white shrink-0"
                     style={{ background: "linear-gradient(135deg, #B91C1C, #EA580C)" }}>
                    <UserCog size={16} />
                </div>
                <div>
                    <div className="font-serif text-base leading-tight">Impersonate User</div>
                    <div className="text-xs text-[var(--text-ink)]/60">
                        Reproduce customer-reported bugs without asking for their password. 30-min signed session, audit-logged.
                    </div>
                </div>
            </div>

            <div className="relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-ink)]/45 pointer-events-none" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by name or email…"
                    data-testid="impersonate-search-input"
                    className="w-full pl-8 pr-3 py-2 rounded-lg border border-[rgba(26,19,24,0.15)] bg-white/80 text-sm"
                />
            </div>

            {err && (
                <div className="mt-2 text-xs text-red-600 flex items-center gap-1" data-testid="impersonate-error">
                    <AlertCircle size={12} /> {typeof err === "string" ? err : "Failed"}
                </div>
            )}

            {q.trim() && (
                <div className="mt-3 divide-y divide-[rgba(26,19,24,0.06)]" data-testid="impersonate-results">
                    {busy && <div className="py-3 text-xs text-[var(--text-ink)]/55">Searching…</div>}
                    {!busy && rows.length === 0 && (
                        <div className="py-3 text-xs text-[var(--text-ink)]/55" data-testid="impersonate-empty">
                            No users match “{q}”.
                        </div>
                    )}
                    {rows.map((r) => (
                        <div key={r.user_id}
                             data-testid={`impersonate-row-${r.user_id}`}
                             className="py-2.5 flex items-center gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium truncate">
                                    {r.name || "—"}
                                    {r.blocked && (
                                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-red-700 bg-red-100/60 rounded-full px-1.5 py-0.5">
                                            <ShieldOff size={9} /> {r.blocked_reason || "Blocked"}
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-[var(--text-ink)]/60 truncate font-mono">{r.email}</div>
                                <div className="text-[10px] text-[var(--text-ink)]/50 mt-0.5">
                                    <span className="capitalize">{r.role || "?"}</span>
                                    <span className="mx-1.5">·</span>
                                    <span>{r.subscription_status}</span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => impersonate(r)}
                                disabled={r.blocked}
                                data-testid={`impersonate-btn-${r.user_id}`}
                                className={`shrink-0 text-xs px-3 py-1.5 rounded-md border ${
                                    r.blocked
                                        ? "border-[rgba(26,19,24,0.10)] text-[var(--text-ink)]/35 cursor-not-allowed"
                                        : "border-transparent text-white hover:brightness-105"
                                }`}
                                style={r.blocked ? undefined : {
                                    background: "linear-gradient(92deg, #B91C1C, #EA580C)",
                                }}
                            >
                                Impersonate
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


function UserManagementPanel() {
    const [users, setUsers] = useState(null);
    const [busy, setBusy] = useState({});
    const [err, setErr] = useState("");
    const [filter, setFilter] = useState("");

    const load = useCallback(() => {
        api.get("/admin/users?limit=200")
            .then((r) => setUsers(r.data.users || []))
            .catch((e) => setErr(e.response?.data?.detail || e.message));
    }, []);
    useEffect(() => { load(); }, [load]);

    const extend = async (uid) => {
        setBusy((b) => ({ ...b, [uid]: "extend" })); setErr("");
        try {
            const { data } = await api.post(`/admin/users/${uid}/extend-trial`);
            setUsers((xs) => xs.map((u) => u.user_id === uid
                ? { ...u, trial_end: data.trial_end, subscription_status: data.subscription_status } : u));
        } catch (e) { setErr(e.response?.data?.detail || e.message); }
        finally { setBusy((b) => { const c = { ...b }; delete c[uid]; return c; }); }
    };
    const cancel = async (uid) => {
        if (!window.confirm("Cancel this user's subscription? They'll drop to read-only immediately.")) return;
        setBusy((b) => ({ ...b, [uid]: "cancel" })); setErr("");
        try {
            const { data } = await api.post(`/admin/users/${uid}/cancel-subscription`);
            setUsers((xs) => xs.map((u) => u.user_id === uid
                ? { ...u, subscription_status: data.subscription_status } : u));
        } catch (e) { setErr(e.response?.data?.detail || e.message); }
        finally { setBusy((b) => { const c = { ...b }; delete c[uid]; return c; }); }
    };

    const rows = (users || []).filter((u) => {
        if (!filter) return true;
        const q = filter.toLowerCase();
        return (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q);
    });

    return (
        <div className="card-warm p-5 mb-6" data-testid="user-management-panel">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-[var(--text-ink)]/55">User management</div>
                    <div className="font-serif text-lg leading-tight">Users &amp; billing</div>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Filter by name or email"
                        data-testid="user-mgmt-filter"
                        className="text-sm px-3 py-1.5 rounded-full border border-[rgba(26,19,24,0.12)] w-56 max-w-full"
                    />
                    <button onClick={load} className="btn-ghost text-xs" data-testid="user-mgmt-refresh">Refresh</button>
                </div>
            </div>

            {err && <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2 break-words" data-testid="user-mgmt-error">{err}</div>}

            {users === null ? (
                <div className="text-sm text-[var(--text-ink)]/60">Loading users…</div>
            ) : rows.length === 0 ? (
                <div className="text-sm text-[var(--text-ink)]/60">No users match.</div>
            ) : (
                <div className="overflow-x-auto -mx-2">
                    <table className="w-full text-sm min-w-[720px]">
                        <thead className="text-[10px] uppercase tracking-wide text-[var(--text-ink)]/50">
                            <tr>
                                <th className="text-left px-2 py-2">Name</th>
                                <th className="text-left px-2 py-2">Email</th>
                                <th className="text-left px-2 py-2">Type</th>
                                <th className="text-left px-2 py-2">Status</th>
                                <th className="text-left px-2 py-2">Trial ends</th>
                                <th className="text-left px-2 py-2">Source</th>
                                <th className="text-left px-2 py-2">Referrals</th>
                                <th className="text-right px-2 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((u) => (
                                <tr key={u.user_id} className="border-t border-[rgba(26,19,24,0.06)]" data-testid={`user-row-${u.user_id}`}>
                                    <td className="px-2 py-2 truncate max-w-[160px]" title={u.name}>{u.name || "—"}{u.is_founder && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full brand-gradient text-white uppercase tracking-wider">Founder</span>}</td>
                                    <td className="px-2 py-2 truncate max-w-[220px]" title={u.email}>{u.email}</td>
                                    <td className="px-2 py-2 capitalize">{u.role || "—"}</td>
                                    <td className="px-2 py-2"><StatusPill status={u.subscription_status} /></td>
                                    <td className="px-2 py-2 text-[var(--text-ink)]/70">{fmtDate(u.trial_end)}</td>
                                    <td className="px-2 py-2 text-[var(--text-ink)]/70" data-testid={`user-source-${u.user_id}`}>{u.signup_source || "—"}</td>
                                    <td className="px-2 py-2 tabular-nums" data-testid={`user-referrals-${u.user_id}`} title={u.referral_code ? `Code: ${u.referral_code}` : "No code yet"}>{u.referrals_count ?? 0}</td>
                                    <td className="px-2 py-2 text-right whitespace-nowrap">
                                        <button
                                            onClick={() => extend(u.user_id)}
                                            disabled={!!busy[u.user_id]}
                                            className="btn-ghost text-xs mr-2"
                                            data-testid={`user-extend-${u.user_id}`}
                                        >
                                            {busy[u.user_id] === "extend" ? "…" : "+7 days"}
                                        </button>
                                        <button
                                            onClick={() => cancel(u.user_id)}
                                            disabled={!!busy[u.user_id] || u.is_founder || u.subscription_status === "cancelled"}
                                            className="text-xs px-3 py-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                            data-testid={`user-cancel-${u.user_id}`}
                                            title={u.is_founder ? "Founders cannot be cancelled" : "Cancel subscription"}
                                        >
                                            {busy[u.user_id] === "cancel" ? "…" : "Cancel"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function StatusPill({ status }) {
    const s = status || "trialing";
    const map = {
        active:    { c: "text-green-700 bg-green-100" },
        trialing:  { c: "text-purple-700 bg-purple-100" },
        past_due:  { c: "text-orange-700 bg-orange-100" },
        cancelled: { c: "text-red-700 bg-red-100" },
    };
    const m = map[s] || { c: "text-[var(--text-ink)]/70 bg-[rgba(26,19,24,0.06)]" };
    return <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${m.c}`}>{s.replace("_", " ")}</span>;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return "—";
        return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch { return "—"; }
}
